/**
 * Pure selectors over time-series-linkage run history (fim-service
 * `GET /sites/{site}/matching/runs`). Kept free of React/SWR so the
 * comparison logic can be unit-tested directly (see `matchingRuns.test.ts`).
 */
import { t } from '@lingui/core/macro'

import type { MatchRunCounts, MatchRunView } from './types'

/** Total datapoints linked across every tier this run. */
export function totalLinked(counts: MatchRunCounts | undefined): number {
  return Object.values(counts?.linked ?? {}).reduce((sum, n) => sum + n, 0)
}

/** Newest first by `started` (ISO string compare). Non-mutating. */
export function sortMatchRuns(runs: MatchRunView[]): MatchRunView[] {
  return [...runs].sort((a, b) =>
    (b.started ?? '').localeCompare(a.started ?? '')
  )
}

/**
 * The run to measure `run` against: the newest `done` run started BEFORE it.
 * Matching runs are how the matcher is iterated, so the interesting question
 * per run is "what changed since the last complete one".
 */
export function previousComparableRun(
  runs: MatchRunView[],
  run: MatchRunView
): MatchRunView | undefined {
  return sortMatchRuns(runs).find(
    (r) =>
      r.run_id !== run.run_id &&
      r.status === 'done' &&
      (r.started ?? '') < (run.started ?? '')
  )
}

export interface MatchRunDelta {
  /** Per-tier linked-count change vs the previous run (only non-zero tiers). */
  byTier: [string, number][]
  totalLinked: number
  subjectsPatched: number
  unmatched: number
  ambiguous: number
}

/** Per-tier and headline stat deltas of `run` minus `previous`. */
export function matchRunDelta(
  run: MatchRunView,
  previous: MatchRunView
): MatchRunDelta {
  const tiers = new Set([
    ...Object.keys(run.counts.linked ?? {}),
    ...Object.keys(previous.counts.linked ?? {}),
  ])
  const byTier: [string, number][] = [...tiers]
    .sort()
    .map((tier): [string, number] => [
      tier,
      (run.counts.linked?.[tier] ?? 0) - (previous.counts.linked?.[tier] ?? 0),
    ])
    .filter(([, d]) => d !== 0)
  return {
    byTier,
    totalLinked: totalLinked(run.counts) - totalLinked(previous.counts),
    subjectsPatched: run.subjects_patched - previous.subjects_patched,
    unmatched: (run.counts.unmatched ?? 0) - (previous.counts.unmatched ?? 0),
    ambiguous: (run.counts.ambiguous ?? 0) - (previous.counts.ambiguous ?? 0),
  }
}

/** Human duration between a run's started/ended stamps ("4m 32s"), or null. */
export function runDuration(run: MatchRunView): string | null {
  if (!run.started || !run.ended) return null
  const ms = new Date(run.ended).getTime() - new Date(run.started).getTime()
  if (!Number.isFinite(ms) || ms < 0) return null
  const totalSeconds = Math.round(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return minutes > 0 ? t`${minutes}m ${seconds}s` : t`${seconds}s`
}
